import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TableComponent } from '../../../../shared/component/table/table.component';
import { AgGridTableService } from '../../../../shared/services/ag-grid.service';
import { ReportPaginationService, ReportPayload } from '../../Reports/report-pagination.service';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-evc-recharge-history',
  standalone: true,
  imports: [CommonModule, TableComponent],
  providers: [AgGridTableService, ReportPaginationService],
  template: `
    <div class="evc-section">
      <div class="evc-sec-head">
        <div class="evc-sec-title">
          <span class="evc-sec-accent"></span>
          <div>
            <span class="evc-sec-name">EV Recharge History&nbsp;</span>
            <span class="evc-sec-sub">Top-up transactions & wallet balance credits</span>
          </div>
        </div>
        <div class="view-toggle">
          <button class="vt-btn" [class.vt-active]="viewMode === 'grid'" (click)="setView('grid')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> Grid
          </button>
          <button class="vt-btn" [class.vt-active]="viewMode === 'chart'" (click)="setView('chart')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> Chart
          </button>
        </div>
      </div>
      <div class="evc-content-area">
        <div class="evc-grid-wrap" [class.view-hidden]="viewMode !== 'grid'">
           <app-table 
             [pageNation]="false" 
             [DateFilterShow]="true" 
             [paginationPageSizeSelector]="[10, 20, 50]" 
             [maxRowCount]="totalCount" 
             (onPageChange)="handlePageChange($event)" 
             [ExportFileName]="exportFileName"
             (filtersApplied)="onFiltersApplied($event)">
           </app-table>
        </div>
        <div class="evc-chart-wrap" [class.view-hidden]="viewMode !== 'chart'">
          <canvas #chartCanvas class="evc-canvas"></canvas>
        </div>
      </div>
    </div>
  `,
  styleUrl: '../ev-charger.component.scss'
})
export class EvcRechargeHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locationId: string = '';
  @Input() siteId: string = '';
  @Input() evName?: string;

  @ViewChild('chartCanvas', { static: false }) chartCanvasRef?: ElementRef<HTMLCanvasElement>;

  viewMode: 'grid' | 'chart' = 'grid';
  chartInstance?: Chart;
  private rowData: any[] = [];
  fromDate: string = '';
  toDate: string = '';
  totalCount: number = 0;

  get exportFileName(): string {
    const namePart = this.evName ? ` ${this.evName}` : '';
    return `EV Recharge History${namePart} ${new Date().toISOString().slice(0, 10)}`;
  }

  private destroy$ = new Subject<void>();
  private isDestroyed = false;
  private renderTimer: any = null;

  constructor(
    private tableService: AgGridTableService,
    public reportPaginationService: ReportPaginationService,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    if (!this.locationId) {
      this.locationId = localStorage.getItem('login_id') || localStorage.getItem('location_id') || '9440991006';
    }
    if (!this.siteId) {
      this.siteId = sessionStorage.getItem('consumerSiteId') || localStorage.getItem('site_idLogin') || localStorage.getItem('site_id') || '59.64062087';
    }

    const dTo = new Date();
    const dFrom = new Date();
    dFrom.setDate(dTo.getDate() - 1);
    this.fromDate = dFrom.toISOString().split('T')[0];
    this.toDate = dTo.toISOString().split('T')[0];

    this.reportPaginationService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.totalCount = count;
      });

    this.tableService.tableData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (this.isDestroyed) return;
        this.rowData = (state?.rowData || []).filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
        if (this.viewMode === 'chart' && this.rowData.length > 0) {
          if (this.renderTimer) clearTimeout(this.renderTimer);
          this.renderTimer = setTimeout(() => this.renderChart(), 80);
        }
      });

    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    const locChange = changes['locationId'];
    const siteChange = changes['siteId'];
    if ((locChange && !locChange.firstChange && locChange.currentValue) ||
      (siteChange && !siteChange.firstChange && siteChange.currentValue)) {
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
  }

  setView(mode: 'grid' | 'chart') {
    this.viewMode = mode;
    if (mode === 'chart') {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.renderChart(), 80);
    }
  }

  onFiltersApplied(event: { from_date: string; to_date: string; site_id?: string }) {
    if (event.from_date && event.to_date) {
      this.fromDate = event.from_date;
      this.toDate = event.to_date;
      if (event.site_id) {
        this.siteId = event.site_id;
      }
      this.loadData();
    }
  }

  handlePageChange(event: { page: number; pageSize: number }) {
    this.reportPaginationService.changePage(event, this.destroy$);
  }

  loadData() {
    const servicePayload: ReportPayload = {
      from_date: this.fromDate,
      to_date: this.toDate,
      site_ids: this.siteId,
      loc_id: this.locationId,
      is_paginate: 'N',
    };

    this.reportPaginationService.initFilters(
      servicePayload,
      'RECHARGE_HISTORY',
      this.destroy$,
      '',
      'Reports',
      null,
      false
    );
  }

  renderChart() {
    if (this.isDestroyed) return;
    const canvas = this.chartCanvasRef?.nativeElement;
    if (!canvas || !canvas.isConnected) return;
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }

    const nonDummyRows = this.rowData.filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
    if (nonDummyRows.length === 0) return;
    const labels = nonDummyRows.map(r => r['date'] || r['recharge_date'] || r['Date'] || r['recharge_time'] || 'TXN');
    const netAmountData = nonDummyRows.map(r => parseFloat(r['net_amount'] ?? r['amount'] ?? r['Amount'] ?? r['recharge_amount'] ?? r['amt'] ?? 0));

    this.ngZone.runOutsideAngular(() => {
      if (this.isDestroyed || !canvas.isConnected) return;
      this.chartInstance = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Net Amount (₹)',
            data: netAmountData,
            borderColor: '#e9b84a',
            backgroundColor: 'rgba(233, 184, 74, 0.15)',
            tension: 0.35,
            fill: true,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#e9b84a',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                boxWidth: 14,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = context.parsed.y ?? 0;
                  return ` Net Amount: ₹${value.toLocaleString()}`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(200, 200, 200, 0.15)' },
              ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 15 }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(200, 200, 200, 0.15)' },
              title: { display: true, text: 'Net Amount (₹)' }
            }
          }
        }
      });
    });
  }
}
